import React, { useMemo, useState } from "react";
import { Button, Card, Form } from "react-bootstrap";
import { doc, updateDoc } from "@firebase/firestore";
import { db } from "../../../firebase/FirebaseProvider";
import { Location } from "../../types/Types";

interface Props {
    locations: Location[];
}

interface InputLine {
    id: number;
    text: string;
}

interface MatchResult {
    location?: Location;
    reason?: string;
    matchedName?: string;
    placeText?: string;
}

const MONTHS: Record<string, string> = {
    jan: "01",
    feb: "02",
    mar: "03",
    apr: "04",
    may: "05",
    jun: "06",
    jul: "07",
    aug: "08",
    sep: "09",
    oct: "10",
    nov: "11",
    dec: "12"
};

function normaliseText(value: string): string {
    return value
        .toLowerCase()
        .replace(/&/g, " and ")
        .replace(/[^a-z0-9]+/g, " ")
        .replace(/\bthe\b/g, " ")
        .replace(/\bwetherspoons?\b/g, " ")
        .trim()
        .replace(/\s+/g, " ");
}

function parseOpeningDate(dateText: string): string | undefined {
    const match = dateText.match(/^(\d{2})-([A-Za-z]{3})-(\d{2})$/);
    if (!match) {
        return undefined;
    }

    const day = match[1];
    const month = MONTHS[match[2].toLowerCase()];
    const yearNumber = Number(match[3]);
    if (!month || Number.isNaN(yearNumber)) {
        return undefined;
    }

    const year = yearNumber >= 50 ? 1900 + yearNumber : 2000 + yearNumber;
    return `${year}-${month}-${day}`;
}

function getLocationPlaceText(location: Location): string {
    return normaliseText([
        location.city,
        location.address,
        location.addressLine1,
        location.postcode
    ].filter(Boolean).join(" "));
}

function getPlaceScore(placeText: string, location: Location): number {
    const placeTokens = placeText.split(" ").filter(token => token.length > 2);
    if (placeTokens.length === 0) {
        return 0;
    }

    const locationPlaceText = getLocationPlaceText(location);
    const matchedTokens = placeTokens.filter(token => locationPlaceText.includes(token));

    return matchedTokens.length / placeTokens.length;
}

function findLocation(searchName: string, locations: Location[]): MatchResult {
    const normalisedSearchName = normaliseText(searchName);
    const searchTokens = normalisedSearchName.split(" ").filter(Boolean);
    let matchedName = "";
    let matchedLocations: Location[] = [];

    searchTokens.forEach((_, index) => {
        const prefix = searchTokens.slice(0, index + 1).join(" ");
        const locationsForPrefix = locations.filter(location => normaliseText(location.name || "") === prefix);

        if (locationsForPrefix.length > 0 && prefix.length > matchedName.length) {
            matchedName = prefix;
            matchedLocations = locationsForPrefix;
        }
    });

    if (!matchedName || matchedLocations.length === 0) {
        return { reason: "No matching pub name found at the start of the row" };
    }

    const placeText = searchTokens.slice(matchedName.split(" ").length).join(" ");

    if (matchedLocations.length === 1) {
        return { location: matchedLocations[0], matchedName, placeText };
    }

    const placeMatches = matchedLocations
        .map(location => ({
            location,
            score: getPlaceScore(placeText, location)
        }))
        .filter(match => match.score > 0)
        .sort((a, b) => b.score - a.score);

    if (placeMatches.length === 1 || (placeMatches.length > 1 && placeMatches[0].score > placeMatches[1].score)) {
        return { location: placeMatches[0].location, matchedName, placeText };
    }

    if (placeMatches.length > 1) {
        return { reason: `Ambiguous place match: ${placeMatches.map(match => match.location.name).join(" / ")}` };
    }

    return { reason: `Ambiguous pub name match: ${matchedLocations.map(match => match.name).join(" / ")}` };
}

function AdminApplyOpeningDates(props: Props) {
    const [inputValue, setInputValue] = useState<string>("");
    const [processing, setProcessing] = useState<boolean>(false);
    const [messages, setMessages] = useState<string[]>([]);

    const inputLines = useMemo<InputLine[]>(() => {
        return inputValue
            .split("\n")
            .map((line, index) => ({ id: index, text: line.trim() }))
            .filter(line => line.text.length > 0);
    }, [inputValue]);

    const appendMessage = (message: string) => {
        setMessages(prevMessages => [...prevMessages, message]);
    };

    const removeProcessedLines = (processedLineIds: Set<number>) => {
        setInputValue(prevValue => prevValue
            .split("\n")
            .filter((_, index) => !processedLineIds.has(index))
            .join("\n")
        );
    };

    const applyDates = async () => {
        const lines = inputLines;
        const processedLineIds = new Set<number>();

        if (lines.length === 0) {
            setMessages(["No rows to process."]);
            return;
        }

        setProcessing(true);
        setMessages([`Starting opening date import: ${lines.length} rows.`]);

        for (let index = 0; index < lines.length; index++) {
            const line = lines[index];
            const lineNumber = index + 1;
            appendMessage(`Line ${lineNumber} of ${lines.length}: ${line.text}`);

            const dateText = line.text.slice(-9);
            const openingDate = parseOpeningDate(dateText);
            const searchName = line.text.slice(0, -9).trim();

            if (!openingDate || !searchName) {
                appendMessage(`Line ${lineNumber}: skipped, expected format "Pub Name 09-Dec-79".`);
                continue;
            }

            const match = findLocation(searchName, props.locations);
            if (!match.location) {
                appendMessage(`Line ${lineNumber}: skipped, ${match.reason}.`);
                continue;
            }

            if (match.location.openingDate) {
                processedLineIds.add(line.id);
                removeProcessedLines(processedLineIds);
                continue;
            }

            appendMessage(`Line ${lineNumber}: matched ${match.location.name}${match.placeText ? ` (${match.placeText})` : ""}; updating to ${openingDate}.`);
            const locationRef = doc(db, "locations", match.location.id);
            await updateDoc(locationRef, { openingDate });

            processedLineIds.add(line.id);
            removeProcessedLines(processedLineIds);
            appendMessage(`Line ${lineNumber}: complete.`);
        }

        appendMessage("Finished opening date import.");
        setProcessing(false);
    };

    return (
        <Card className="mb-3">
            <Card.Header>
                <b>Apply Opening Dates</b>
            </Card.Header>
            <Card.Body>
                <Form.Control
                    as="textarea"
                    rows={8}
                    value={inputValue}
                    onChange={(event) => setInputValue(event.target.value)}
                    disabled={processing}
                    placeholder="Paste pub data here"
                />
                <div className="text-right mt-2">
                    <Button onClick={applyDates} disabled={processing || inputLines.length === 0}>
                        {processing ? "Applying..." : "Apply Dates"}
                    </Button>
                </div>
                {messages.length > 0 && (
                    <div className="alert alert-info mt-3 mb-0">
                        {messages.map((message, index) => (
                            <div key={index}>{message}</div>
                        ))}
                    </div>
                )}
            </Card.Body>
        </Card>
    );
}

export default AdminApplyOpeningDates;
